/**
 * tickets-v2-api — the one request path for the Tickets v2 screens (ticket-groups-v2, tax, fees,
 * relationship tiers, audit entries, events list). Every call goes through the @v0y4ge/api-client
 * `apiRaw` choke point with the operationId resolved from OPERATIONS_REGISTRY — a path that isn't a
 * registered operation throws instead of being called. Local only because api-client is kit-owned;
 * typed per-op client methods are requested in CLAUDE-CODE-REQUESTS.md (REQ-134 §10).
 */
import { apiRaw, isApiError } from "@v0y4ge/api-client";
import { OPERATIONS_REGISTRY } from "@v0y4ge/api-contracts";
import { reportOpSource } from "../../../shell/opSource";
import { can } from "../../../shell/access";
import { t } from "../../../lib/i18n";

/** Capability an admin write needs (ruling KP 2026-09-30: ticket groups + fee stay on products.write; tax is billing.write). */
export function tv2WriteCapability(method: string, path: string): string | null {
  if (method.toUpperCase() === "GET" || !path.startsWith("/v1/admin/")) return null;
  if (/^\/v1\/admin\/tax\//.test(path)) return "billing.write";
  if (/^\/v1\/admin\/events\//.test(path)) return "events.write";
  return "products.write";
}

type OpRow = { id: string; method: string; re: RegExp };
let ROWS: OpRow[] | null = null;
const rows = (): OpRow[] => {
  if (ROWS) return ROWS;
  ROWS = Object.entries(OPERATIONS_REGISTRY as Record<string, { method?: string; path?: string; callId?: string }>)
    .filter(([, d]) => d && d.method && d.path)
    .map(([k, d]) => ({
      id: d.callId || k,
      method: String(d.method).toUpperCase(),
      re: new RegExp("^" + String(d.path).replace(/[.*+?^$()|[\]\\]/g, "\\$&").replace(/\\?\{[^}]+\\?\}/g, "[^/]+").replace(/\{[^}]+\}/g, "[^/]+") + "$"),
    }));
  return ROWS;
};

export function tv2OperationFor(method: string, path: string): string | null {
  const bare = path.split("?")[0];
  const m = method.toUpperCase();
  const hit = rows().find((r) => r.method === m && r.re.test(bare));
  return hit ? hit.id : null;
}

/** Design-environment only: the relationship tier the Buyer's view previews as (header x-cd-preview-as-tier). */
let previewAsTier = "";
export function setPreviewAsTier(tier: string) { previewAsTier = tier || ""; }

export interface Tv2Error extends Error { status?: number; code?: string }

export async function tv2Call<T = any>(method: string, path: string, body?: unknown): Promise<T> {
  const op = tv2OperationFor(method, path);
  if (!op) throw Object.assign(new Error(`${method} ${path.split("?")[0]} is not a registered operation.`), { status: 0, code: "UNREGISTERED_OPERATION" }) as Tv2Error;
  const cap = tv2WriteCapability(method, path);
  if (cap && !can(cap)) throw Object.assign(new Error(t("tickets.v2.api.roleCantChange", "Your role can’t make this change.")), { status: 403, code: "FORBIDDEN" }) as Tv2Error;
  const headers: Record<string, string> = {};
  if (previewAsTier && path.startsWith("/v1/public/")) headers["x-cd-preview-as-tier"] = previewAsTier;
  try {
    const r = await apiRaw<T>(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) }, op);
    reportOpSource(op, r);
    return r;
  } catch (e) {
    if (isApiError(e)) {
      const err = new Error(e.message) as Tv2Error;
      err.status = e.status; err.code = e.code;
      throw err;
    }
    throw e;
  }
}
